/**
 * The period every report is read over, kept apart from the API calls so a test, or a link
 * back to a report (`back.ts`), can read it without a browser.
 */

/**
 * The presets, by the names the API accepts.
 *
 * Resolved on the server, in the firm's own timezone, and the resolved dates come back
 * with every answer. A laptop in the back office is set to whichever timezone somebody
 * chose when they installed Windows, and "last month" resolved there would be a different
 * month from the one the report was computed over.
 */
export const periodPresets = [
  'thisWeek',
  'lastWeek',
  'thisMonth',
  'lastMonth',
  'lastThreeMonths',
  'custom',
] as const;

export type PeriodPreset = (typeof periodPresets)[number];

export interface Period {
  preset: PeriodPreset;
  /** `yyyy-MM-dd`, and only used when the preset is `custom`. */
  from: string;
  to: string;
}

/** What a report opens on, with today filled in for a custom range. */
export function defaultPeriod(preset: PeriodPreset): Period {
  const today = new Date().toISOString().slice(0, 10);

  return { preset, from: today, to: today };
}

/** The query every report takes. */
export function periodQuery(period: Period, format?: 'csv'): string {
  const parts = [`preset=${period.preset}`];

  if (period.preset === 'custom') {
    parts.push(`from=${period.from}`, `to=${period.to}`);
  }

  if (format !== undefined) parts.push(`format=${format}`);

  return parts.join('&');
}
